import React, { useState, useEffect, useCallback } from "react";
import {
  Space,
  Row,
  Col,
  Spin,
  Table,
  Tag,
  Typography,
  Button,
  Tooltip,
  Empty,
  Tabs,
} from "antd";
import type { TableColumnsType } from "antd";
import { ValidationService } from "../../../services/ValidationService";
import { usePermissions } from "../../../PermissionsProvider";
import { useParams, Link } from "react-router-dom";
import { ValidationRun } from "../../../model_gen/finetuning";
import {
  FileTextOutlined,
  RocketOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  AppstoreOutlined,
  FileSearchOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const ValidationRunView: React.FC = () => {
  const { apiKey } = usePermissions();
  const { validationRunId } = useParams();
  const [validationRun, setValidationRun] = useState<ValidationRun>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("overview");

  const styles = {
    hoverRow: {
      cursor: "pointer",
      transition: "background-color 0.2s ease",
    },
    monospaceFont: {
      fontFamily: "monospace",
      fontSize: "13px",
    },
    statsCard: (color: string) => ({
      background: `linear-gradient(135deg, ${color}10 0%, #f8f8f8 100%)`,
      padding: "14px 16px",
      borderRadius: "12px",
      height: "100%",
      border: `1px solid ${color}30`,
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
    }),
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    detailsContainer: {
      borderRadius: "12px",
      border: "1px solid #f0f0f0",
      overflow: "hidden",
    },
    detailsHeader: {
      borderBottom: "1px solid #f0f0f0",
      padding: "16px 24px",
      background: "#fafafa",
    },
    detailsContent: {
      padding: "24px",
    },
    subtitle: {
      fontSize: "14px",
      color: "#8c8c8c",
      display: "block",
      marginBottom: "8px",
    },
    tagStyle: {
      padding: "4px 10px",
      borderRadius: "4px",
      fontSize: "12px",
    },
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "success";
      case "FAILED":
        return "error";
      case "RUNNING":
        return "processing";
      default:
        return "default";
    }
  };

  const fetchData = useCallback(
    (isRefreshing: boolean) => {
      if (!validationRunId) return;
      const loadingHandler = isRefreshing ? null : setIsLoading;
      ValidationService.getValidationRequests(
        validationRunId,
        loadingHandler,
        apiKey
      ).subscribe(
        (data: ValidationRun) => {
          setValidationRun(data);
        },
        (error: any) => {
          console.error("Error fetching validation requests:", error);
        }
      );
    },
    [apiKey, validationRunId]
  );

  useEffect(() => {
    fetchData(false);

    // Set up auto-refresh interval
    const intervalId = setInterval(() => {
      fetchData(true);
    }, 15000);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchData]);

  // Add global CSS for interactive rows
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
      .details-button:hover {
        background-color: #f0f7ff !important;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1) !important;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        background-color: #f5f8ff !important;
      }
      .ant-statistic-content-value {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (isLoading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  const requestColumns: TableColumnsType<any> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
      sorter: (a: any, b: any) => a.date_created - b.date_created,
    },
    {
      title: "Request ID",
      dataIndex: "request_id",
      key: "request_id",
      width: "15%",
      render: (text: string) => (
        <Text style={styles.monospaceFont}>{text}</Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "correct",
      key: "correct",
      width: "10%",
      render: (correct: boolean) => (
        <Tag color={correct ? "success" : "error"} style={styles.tagStyle}>
          {correct ? "Correct" : "Incorrect"}
        </Tag>
      ),
      sorter: (a: any, b: any) => {
        if (a.correct === b.correct) return 0;
        return a.correct ? 1 : -1;
      },
    },
    {
      title: "Expected Output",
      dataIndex: "expected_output",
      key: "expected_output",
      width: "25%",
      render: (text: string) => (
        <div
          style={{
            whiteSpace: "pre-wrap",
            maxHeight: "150px",
            overflow: "auto",
          }}>
          {text}
        </div>
      ),
    },
    {
      title: "Actual Output",
      dataIndex: "actual_output",
      key: "actual_output",
      width: "25%",
      render: (text: string) => (
        <div
          style={{
            whiteSpace: "pre-wrap",
            maxHeight: "150px",
            overflow: "auto",
          }}>
          {text}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      align: "center" as const,
      render: () => (
        <Button
          type="text"
          className="details-button"
          style={{
            borderRadius: "6px",
            color: "#1890ff",
            display: "flex",
            alignItems: "center",
            padding: "0 10px",
            height: "32px",
            transition: "all 0.2s",
          }}
          icon={<EyeOutlined style={{ marginRight: "4px" }} />}>
          Details
        </Button>
      ),
    },
  ];

  const renderOverviewTab = () => (
    <Row gutter={[24, 24]}>
      {/* Stats Row */}
      <Col span={24}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <div style={styles.statsCard("#1890ff")}>
              <Space>
                <FileTextOutlined
                  style={{ color: "#1890ff", fontSize: "24px" }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: "12px", display: "block" }}>
                    Total Requests
                  </Text>
                  <Text strong style={{ fontSize: "18px" }}>
                    {validationRun?.total_requests || 0}
                  </Text>
                </div>
              </Space>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={styles.statsCard("#52c41a")}>
              <Space>
                <CheckCircleOutlined
                  style={{ color: "#52c41a", fontSize: "24px" }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: "12px", display: "block" }}>
                    Accuracy
                  </Text>
                  <Text strong style={{ fontSize: "18px" }}>
                    {validationRun?.accuracy !== undefined
                      ? `${((validationRun?.accuracy || 0) * 100).toFixed(2)}%`
                      : "N/A"}
                  </Text>
                </div>
              </Space>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={styles.statsCard("#722ed1")}>
              <Space>
                <RocketOutlined
                  style={{ color: "#722ed1", fontSize: "24px" }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: "12px", display: "block" }}>
                    Status
                  </Text>
                  <Tag
                    color={getStatusColor(validationRun?.status || "")}
                    style={styles.tagStyle}>
                    {validationRun?.status?.toUpperCase() || "N/A"}
                  </Tag>
                </div>
              </Space>
            </div>
          </Col>
        </Row>
      </Col>

      {/* Details Section */}
      <Col span={24}>
        <div style={styles.detailsContainer}>
          <div style={styles.detailsHeader}>
            <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
              Details
            </Title>
          </div>
          <div style={styles.detailsContent}>
            <Row gutter={[0, 24]}>
              <Col span={24}>
                <Text style={styles.subtitle}>Company</Text>
                <Text>{validationRun?.company_name || "N/A"}</Text>
              </Col>
              {validationRun?.application_name && (
                <Col span={24}>
                  <Text style={styles.subtitle}>Application</Text>
                  <Text>{validationRun?.application_name}</Text>
                </Col>
              )}
              {validationRun?.intent_name && (
                <Col span={24}>
                  <Text style={styles.subtitle}>Intent</Text>
                  <Text>{validationRun?.intent_name}</Text>
                </Col>
              )}
              <Col span={24}>
                <Text style={styles.subtitle}>Model</Text>
                <Text>{validationRun?.model || "N/A"}</Text>
              </Col>
              <Col span={24}>
                <Text style={styles.subtitle}>Description</Text>
                <Text>{validationRun?.description || "N/A"}</Text>
              </Col>
              <Col span={24}>
                <Text style={styles.subtitle}>Finetune Run</Text>
                {validationRun?.finetune_run_id ? (
                  <Link
                    to={`/build/finetuning/${validationRun?.finetune_run_id}`}>
                    View Finetune Run
                  </Link>
                ) : (
                  <Text type="secondary">N/A</Text>
                )}
              </Col>
            </Row>
          </div>
        </div>
      </Col>
    </Row>
  );

  const renderResultsTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Validation Results
          </Typography.Title>
        </Col>
      </Row>

      <div style={styles.tableContainer}>
        <Table
          dataSource={validationRun?.requests}
          columns={requestColumns}
          rowKey="request_id"
          rowClassName={() => "interactive-row"}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <div>
                    <Text
                      strong
                      style={{ display: "block", marginBottom: "8px" }}>
                      No requests found
                    </Text>
                    <Text type="secondary">
                      No validation requests are available.
                    </Text>
                  </div>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>
    </div>
  );

  return (
    <div
      className="validation-run-container"
      style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: "32px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                <Link to="/build/validation">Validation</Link> / Run Details
              </Typography.Text>
              <Typography.Title
                level={2}
                style={{ margin: 0, fontWeight: 600 }}>
                Validation Results
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                View validation run details and request results
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        className="custom-tabs"
        tabBarStyle={{ marginBottom: "24px" }}
        tabBarGutter={12}
        animated={{ inkBar: true, tabPane: true }}>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <AppstoreOutlined style={{ marginRight: "8px" }} />
              Overview
            </span>
          }
          key="overview">
          <div style={styles.contentCard}>{renderOverviewTab()}</div>
        </TabPane>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <FileSearchOutlined style={{ marginRight: "8px" }} />
              Results
            </span>
          }
          key="results">
          <div style={styles.contentCard}>{renderResultsTab()}</div>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ValidationRunView;
